import {
  AccountFlagsInterface,
  AccountFlagsStateType,
  OnAccountFlagsStateChangedCallback,
} from '@central-products/interface-account-flags';
import {
  ACCOUNT_ACTION_IDS,
  ACCOUNT_EVENT_IDS,
  AccountActionsRequestedCallback,
} from '@central-products/interface-account-actions';
import { StateType as SessionManagerStateType } from '@central-products/interface-session-manager';
import { StateType as SessionServiceStateType } from '@central-products/interface-session-service';
import { subscribe } from '@wh/message-bus';
import {
  ACCOUNT_FLAGS_BASE_STATE_EXTENSION,
  ACCOUNT_FLAGS_DEFAULT_POLLING_INTERVAL,
  SESSION_MANAGER_EVENT_LOGIN,
  SESSION_MANAGER_EVENT_TOKEN_LOGIN,
} from './constants';
import { ACCOUNT_FLAGS_STATUS_FLAGS_QUERY } from './graphql';
import { objectEquals } from './libs/object-equals';
import { processAccountFlags } from './libs/process-account-flags';
import {
  AccountFlagsConfig,
  AccountFlagsDependencies,
  AccountFlagsStateExtensionType,
  FlagType,
  Jurisdiction,
} from './types';

export class AccountFlags extends AccountFlagsInterface {
  private readonly config: AccountFlagsConfig;
  private readonly dependencies: AccountFlagsDependencies;
  private state: AccountFlagsStateType = AccountFlagsStateType.ACCOUNT_FLAGS_UNKNOWN;
  private stateExtension: AccountFlagsStateExtensionType = ACCOUNT_FLAGS_BASE_STATE_EXTENSION;
  private onStateChangedCallback?: OnAccountFlagsStateChangedCallback;
  private abortController?: AbortController;
  private processedAccountFlags: string[] = [];
  private hasSuccessfulLoginExection: boolean = false;

  constructor(config: AccountFlagsConfig, dependencies: AccountFlagsDependencies) {
    super();

    this.config = config;
    this.dependencies = dependencies;
  }

  public async init() {
    /**
     * Not really clean way, but currently there is no way to attach listeners
     * to those session manager commands in object-oriented API way.
     */
    subscribe({ topic: SESSION_MANAGER_EVENT_LOGIN, callback: this.processLoginEvent });
    subscribe({ topic: SESSION_MANAGER_EVENT_TOKEN_LOGIN, callback: this.processLoginEvent });

    this.dependencies['account-actions'].onActionRequested(this.processAction);
    this.dependencies['session-service'].onStateChanged(this.processSessionServiceState);
    const sessionServiceState = await this.dependencies['session-service'].getState();
    this.processSessionServiceState(sessionServiceState);
  }

  public async getState() {
    return { state: this.state, ...this.stateExtension };
  }

  public onStateChanged(callback: OnAccountFlagsStateChangedCallback) {
    this.onStateChangedCallback = callback;
  }

  private processLoginEvent = ({ data: { state } }: { data: { state: SessionManagerStateType } }) => {
    this.hasSuccessfulLoginExection = state === SessionManagerStateType.LOGGED_IN;
  };

  private processAction: AccountActionsRequestedCallback = async ({ actionId, data }) => {
    if (
      actionId !== ACCOUNT_ACTION_IDS.ACCOUNT_FLAG_CLOSE &&
      actionId !== ACCOUNT_ACTION_IDS.ACCOUNT_FLAG_SUCCESS &&
      actionId !== ACCOUNT_ACTION_IDS.ACCOUNT_FLAG_QUIT
    ) {
      return;
    }

    /**
     * We are adding soft flags to list of processed flags in order
     * to prevent situations where after declining soft flag user is
     * presented with same soft flag resolving micro app thus preventing
     * him from being able to resolve any other soft flag or use
     * vertical/application itself.
     */
    if (data.type === FlagType.SOFT) {
      this.processedAccountFlags.push(data.flag);
    }

    /**
     * We do not want to trigger any account flags updates after
     * resolving hard flag or when we know there is no session.
     */
    if (data.type === FlagType.HARD || this.state === AccountFlagsStateType.NO_ACCOUNT_FLAGS) {
      return;
    }

    this.setState(AccountFlagsStateType.ACCOUNT_FLAGS_REQUESTED, { flags: [] });
    const accountFlagsState = await this.fetchAccountFlagsState();
    this.setState(
      accountFlagsState.flags?.length
        ? AccountFlagsStateType.ACCOUNT_FLAGS_EXISTS
        : AccountFlagsStateType.ACCOUNT_FLAGS_EMPTY,
      accountFlagsState
    );
  };

  private processSessionServiceState = ({ state }: { state: SessionServiceStateType }) => {
    if (state === SessionServiceStateType.SESSION_EXISTS) {
      this.processSessionExistsState();
    }

    if (state === SessionServiceStateType.NO_SESSION) {
      this.processNoSessionState();
    }
  };

  private async processSessionExistsState() {
    if (
      this.state !== AccountFlagsStateType.ACCOUNT_FLAGS_UNKNOWN &&
      this.state !== AccountFlagsStateType.NO_ACCOUNT_FLAGS
    ) {
      return;
    }

    this.processedAccountFlags.length = 0;

    if (this.config.jurisdiction === Jurisdiction.COM && !this.hasSuccessfulLoginExection) {
      this.setState(AccountFlagsStateType.ACCOUNT_FLAGS_EMPTY, { flags: [] });
      return;
    }

    try {
      this.setState(AccountFlagsStateType.ACCOUNT_FLAGS_REQUESTED, { flags: [] });
      const accountFlagsState = await this.fetchAccountFlagsState();
      this.setState(
        accountFlagsState.flags?.length
          ? AccountFlagsStateType.ACCOUNT_FLAGS_EXISTS
          : AccountFlagsStateType.ACCOUNT_FLAGS_EMPTY,
        accountFlagsState
      );

      if (this.config.jurisdiction !== Jurisdiction.COM) {
        this.watchAccountFlagsState();
      }
    } catch (error) {
      this.setState(AccountFlagsStateType.NO_ACCOUNT_FLAGS, ACCOUNT_FLAGS_BASE_STATE_EXTENSION);
    }
  }

  private processNoSessionState() {
    if (this.state === AccountFlagsStateType.NO_ACCOUNT_FLAGS) {
      return;
    }

    this.processedAccountFlags.length = 0;

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
    }

    this.setState(AccountFlagsStateType.NO_ACCOUNT_FLAGS, ACCOUNT_FLAGS_BASE_STATE_EXTENSION);
  }

  private async fetchAccountFlagsState() {
    // Setting abort controller for canceling fetching account flags
    // that is being triggered during transition into no session state.
    this.abortController = new AbortController();

    return new Promise<AccountFlagsStateExtensionType>(async (resolve, reject) => {
      try {
        const accountFlagsQuery = await this.dependencies['data-access-service'].query({
          query: ACCOUNT_FLAGS_STATUS_FLAGS_QUERY,
          fetchPolicy: 'no-cache',
          variables: {
            params: { isMobile: this.config.isMobileDevice },
          },
        });

        if (accountFlagsQuery?.data?.statusFlags) {
          const accountFlags = (accountFlagsQuery?.data?.statusFlags as unknown) as {
            name: string;
            type: FlagType;
            token: string | null;
          }[];

          resolve({ flags: processAccountFlags(accountFlags, this.processedAccountFlags) });
        } else {
          reject();
        }

        const onAbortCallback = () => {
          this.abortController?.signal.removeEventListener('abort', onAbortCallback);
          reject();
        };

        this.abortController?.signal.addEventListener('abort', onAbortCallback);
      } catch (error) {
        reject();
      }
    });
  }

  private async watchAccountFlagsState() {
    // Setting abort controller for canceling fetching account flags
    // that is being triggered during transition into no session state.
    this.abortController = new AbortController();

    while (this.abortController?.signal?.aborted === false) {
      await new Promise((resolve) => {
        const onAbortOrResolveCallback = () => {
          this.abortController?.signal.removeEventListener('abort', onAbortOrResolveCallback);
          resolve();
        };
        this.abortController?.signal.addEventListener('abort', onAbortOrResolveCallback);
        window.setTimeout(onAbortOrResolveCallback, ACCOUNT_FLAGS_DEFAULT_POLLING_INTERVAL);
      });

      try {
        const accountFlagsQuery = await this.dependencies['data-access-service'].query({
          query: ACCOUNT_FLAGS_STATUS_FLAGS_QUERY,
          fetchPolicy: 'no-cache',
          variables: {
            params: { isMobile: this.config.isMobileDevice },
          },
        });

        if (accountFlagsQuery?.data?.statusFlags) {
          const accountFlags = (accountFlagsQuery?.data?.statusFlags as unknown) as {
            name: string;
            type: FlagType;
            token: string | null;
          }[];
          const accountFlagsState = { flags: processAccountFlags(accountFlags, this.processedAccountFlags) };

          if (this.abortController?.signal?.aborted === false) {
            this.setState(
              accountFlagsState.flags.length
                ? AccountFlagsStateType.ACCOUNT_FLAGS_EXISTS
                : AccountFlagsStateType.ACCOUNT_FLAGS_EMPTY,
              accountFlagsState
            );
          }
        }
      } catch (error) {}
    }
  }

  private setState = (state: AccountFlagsStateType, stateExtension: AccountFlagsStateExtensionType) => {
    if (this.state === state && objectEquals(this.stateExtension, stateExtension)) {
      return;
    }

    this.state = state;
    this.stateExtension = stateExtension;

    if (this.onStateChangedCallback) {
      this.onStateChangedCallback({ state, ...stateExtension });
    }

    this.processStateChange(state, stateExtension);
  };

  private processStateChange = async (state: AccountFlagsStateType, stateExtension: AccountFlagsStateExtensionType) => {
    if (state === AccountFlagsStateType.ACCOUNT_FLAGS_EXISTS && stateExtension.flags?.length) {
      const [statusFlag] = stateExtension.flags;

      if (statusFlag.type === FlagType.HARD) {
        await this.dependencies['session-manager'].logOut();
      }

      this.dependencies['account-actions'].execute({
        eventId:
          statusFlag.type === 'H' ? ACCOUNT_EVENT_IDS.HARD_FLAG_REQUESTED : ACCOUNT_EVENT_IDS.SOFT_FLAG_REQUESTED,
        context: { data: { flag: statusFlag.name, token: statusFlag.token }, userFlow: 'account-flags' },
      });
    }
  };
}
